import React, { useEffect, useRef, useState } from "react"
import { useStoreActions, useStoreState, StateMapper } from "easy-peasy"
import styled from "styled-components"
import { Button, Col, Input, Row, Skeleton, Tag, Tooltip, message } from "antd"
import { UploadOutlined } from "@ant-design/icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCheck,
  faGear,
  faLock,
  faPenToSquare,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons"
import { AppDataStore } from "../appData/types"
import { screenSizes } from "../styles/GlobalStyles"
import {
  gen,
  swimminglyApi,
} from "./utils"

const TEAM_LOGOS_CHANGED_EVENT = "swimmingly:team-logos:changed"

const GearButton = styled.button<{ alwaysVisible?: boolean }>`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: ${(p) => (p.alwaysVisible ? 1 : 0)};
  transition: opacity 150ms ease, background-color 150ms ease, color 150ms ease;
  color: #55657a;
  z-index: 2;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(37, 191, 234, 0.4);
  }

  &:hover {
    background: var(--primaryblue);
    color: #ffffff;
  }
`

const ClubSummaryWrapper = styled.div`
  max-width: 100%;
  overflow-x: auto;
  border-radius: 10px;
  border: 1px solid #b7c3d0;
  background: rgba(37, 191, 234, 0.04);
  padding: 8px 10px;
  border-left: 4px solid var(--primaryblue);
  position: relative;
  perspective: 1000px;

  &:hover ${GearButton} {
    opacity: 1;
  }

  @media only screen and (max-width: ${screenSizes.small}px) {
    overflow-x: visible;
  }
`

const CardFlipInner = styled.div<{ flipped: boolean; cardHeight: number | null }>`
  position: relative;
  width: 100%;
  height: ${(p) => (p.cardHeight ? `${p.cardHeight}px` : "auto")};
  transform-style: preserve-3d;
  transition: transform 320ms ease;
  transform: rotateY(${(p) => (p.flipped ? 180 : 0)}deg);
`

const CardFace = styled.div<{
  back?: boolean
  flipped?: boolean
  fixedHeight: boolean
}>`
  position: ${(p) => (p.fixedHeight ? "absolute" : "relative")};
  top: 0;
  left: 0;
  width: 100%;
  backface-visibility: hidden;
  transform: ${(p) => {
    if (!p.fixedHeight) {
      return "none"
    }

    return p.back ? "rotateY(180deg)" : "none"
  }};
  display: ${(p) => (!p.fixedHeight && p.back ? "none" : "block")};
  opacity: ${(p) => {
    if (!p.fixedHeight) {
      return p.back ? 0 : 1
    }

    if (p.back) {
      return p.flipped ? 1 : 0
    }
    return p.flipped ? 0 : 1
  }};
  pointer-events: ${(p) => {
    if (!p.fixedHeight) {
      return p.back ? "none" : "auto"
    }

    if (p.back) {
      return p.flipped ? "auto" : "none"
    }
    return p.flipped ? "none" : "auto"
  }};
  transition: opacity 320ms ease;
`

const ClubSummaryLabel = styled.div`
  font-size: calc(0.7rem + 2px);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #55657a;
  margin-bottom: 4px;
  font-weight: 700;
`

const StyledClubTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: "MuseoSansRounded", "Open Sans", Arial, sans-serif;
  font-size: calc(0.95rem + 3px);
  color: #222;

  th,
  td {
    padding: 4px 8px;
    border: 1px solid var(--lightgrey);
    white-space: nowrap;
  }

  th {
    background: #f6f8fa;
    color: #555;
    font-weight: 700;
    text-align: left;
  }

  td {
    color: #222;
    font-weight: 500;
  }

  @media only screen and (max-width: ${screenSizes.small}px) {
    font-size: calc(0.9rem + 3px);

    th,
    td {
      white-space: normal;
    }
  }
`

const InlineEditor = styled.div`
  margin-top: 0;
  padding-top: 0;
  border-top: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: calc(0.95rem + 3px);
`

const InlineField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const InlineLabel = styled.div`
  font-weight: 600;
  color: #222;
`

const InlineHelp = styled.div`
  font-size: calc(0.8rem + 2px);
  color: #6b6b6b;
`

const InlineHelpPill = styled(InlineHelp)`
  margin-top: 9px;
  padding: 0 10px 4px 10px;
  border-radius: 10px;
  background: #f0fff4;
  color: #237804;
  border: 1px solid #b7eb8f;
`

const InlineValueBox = styled.div`
  border-radius: 4px;
  padding: 6px 10px;
  border: 1px solid #d9d9d9;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
  position: relative;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    background-color: #f5f9ff;
    border-color: var(--primaryblue);
  }

  .value-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .edit-icon,
  .lock-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  .edit-icon {
    color: var(--primaryblue);
    opacity: 0;
    transition: opacity 120ms ease;
  }

  &:hover .edit-icon {
    opacity: 1;
  }

  .lock-icon {
    color: #8c8c8c;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 18px;
    width: 18px;
  }

  .lock-icon svg {
    font-size: 14px;
    display: block;
    vertical-align: middle;
    transform: translateY(1px);
  }
`

const InlineEditRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`

const InlineIconButton = styled.button`
  border: none;
  background: transparent;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #8c8c8c;
  cursor: pointer;
  padding: 0;
  transition: background-color 120ms ease, color 120ms ease;

  &:hover {
    background-color: #f0f0f0;
    color: var(--primaryblue);
  }
`

const CredentialsHint = styled.div`
  margin-top: 4px;
  font-size: calc(0.8rem + 2px);
  color: #6b6b6b;
`

const FrontFaceLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 240px) minmax(0, 1fr);
  gap: 10px;
  align-items: stretch;

  @media only screen and (max-width: ${screenSizes.small}px) {
    grid-template-columns: 1fr;
  }
`

const LogoPanel = styled.div`
  min-height: 100%;
  border-radius: 14px;
  border: 1px solid rgba(15, 94, 156, 0.16);
  background:
    radial-gradient(circle at top left, rgba(37, 191, 234, 0.22), rgba(255, 255, 255, 0.98) 55%),
    linear-gradient(165deg, rgba(15, 94, 156, 0.08), rgba(255, 255, 255, 0.96));
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
`

const LogoStage = styled.div<{ empty?: boolean; clickable?: boolean }>`
  min-height: 112px;
  border-radius: 12px;
  border: ${(p) =>
    p.empty
      ? "2px dashed rgba(15, 94, 156, 0.28)"
      : "1px solid rgba(15, 94, 156, 0.12)"};
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(244, 249, 255, 0.92));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  overflow: hidden;
  position: relative;
  cursor: ${(p) => (p.clickable ? "pointer" : "default")};
  transition: border-color 120ms ease, background 120ms ease, transform 120ms ease;

  &:hover {
    border-color: ${(p) =>
      p.clickable ? "rgba(15, 94, 156, 0.42)" : undefined};
    background: ${(p) =>
      p.clickable
        ? "linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(235, 246, 255, 0.96))"
        : undefined};
    transform: ${(p) => (p.clickable ? "translateY(-1px)" : undefined)};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${(p) =>
      p.clickable ? "0 0 0 3px rgba(37, 191, 234, 0.24)" : undefined};
  }

  @media only screen and (max-width: ${screenSizes.small}px) {
    min-height: 96px;
  }
`

const LogoImage = styled.img`
  width: 100%;
  max-width: 100%;
  max-height: 92px;
  object-fit: contain;
  filter: drop-shadow(0 10px 18px rgba(15, 94, 156, 0.12));

  @media only screen and (max-width: ${screenSizes.small}px) {
    max-height: 76px;
  }
`

const LogoStageSkeleton = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-skeleton.ant-skeleton-element,
  .ant-skeleton-image {
    width: 100%;
    height: 92px;
    border-radius: 10px;
  }

  @media only screen and (max-width: ${screenSizes.small}px) {
    .ant-skeleton.ant-skeleton-element,
    .ant-skeleton-image {
      height: 76px;
    }
  }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 4px;
  color: #35506b;
  width: 100%;
  min-height: 92px;

  @media only screen and (max-width: ${screenSizes.small}px) {
    min-height: 76px;
  }
`

const EmptyStateIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(37, 191, 234, 0.14);
  color: #0f5e9c;
  font-size: 18px;
`

const EmptyStateTitle = styled.div`
  font-size: calc(0.72rem + 2px);
  font-weight: 700;
  line-height: 1.1;
  color: #17324d;
  white-space: nowrap;
`

const LogoUnavailableTitle = styled(EmptyStateTitle)`
  white-space: normal;
  max-width: 120px;
  color: #55657a;
`

const ClubSummaryContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

type ReportBrandingOption = {
  id: number
  slotIndex: number
  slotLabel: string
  displayName: string
  imageWidth: number | null
  imageHeight: number | null
  dataUri: string
}

type ReportBrandingContext = {
  approvedLogos: ReportBrandingOption[]
}

export default function ClubBlock() {
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const impersonateClub = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedClub,
  )
  const setAliasedClub = useStoreActions(
    (actions: AppDataStore) => actions.setAliasedClub,
  )

  const [expanded, setExpanded] = useState(false)
  const [nameLong, setNameLong] = useState(impersonateClub?.nameLong || "")
  const [nameShort, setNameShort] = useState(impersonateClub?.name || "")
  const [starterKey, setStarterKey] = useState(
    impersonateClub?.starter_key || "",
  )
  const [saving, setSaving] = useState(false)
  const [editingField, setEditingField] = useState<
    "nameLong" | "nameShort" | "starterKey" | null
  >(null)
  const [showErrors, setShowErrors] = useState(false)
  const [cardHeight, setCardHeight] = useState<number | null>(null)
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false
    return window.innerWidth <= screenSizes.small
  })
  const [reportBrandingContext, setReportBrandingContext] =
    useState<ReportBrandingContext | null>(null)
  const [reportBrandingLoading, setReportBrandingLoading] = useState(false)
  const [reportBrandingError, setReportBrandingError] = useState(false)
  const [isGuestClub, setIsGuestClub] = useState(false)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const frontRef = useRef<HTMLDivElement | null>(null)
  const backRef = useRef<HTMLDivElement | null>(null)

  const loadReportBrandingContext = async (clubId: number) => {
    setReportBrandingLoading(true)
    setReportBrandingError(false)
    try {
      const response: any = await swimminglyApi.get(
        gen(`/api/team-logos/report-branding-context?clubId=${clubId}`),
      )

      if (response?.status === "error") {
        setReportBrandingContext({ approvedLogos: [] })
        setReportBrandingError(true)
        return
      }

      setReportBrandingContext({
        approvedLogos:
          Array.isArray(response?.approvedLogos) ? response.approvedLogos : [],
      })
    } catch (_err) {
      setReportBrandingContext({ approvedLogos: [] })
      setReportBrandingError(true)
    } finally {
      setReportBrandingLoading(false)
    }
  }

  const resetField = (field: "nameLong" | "nameShort" | "starterKey") => {
    if (!impersonateClub) return

    if (field === "nameLong") {
      setNameLong(impersonateClub.nameLong || "")
    } else if (field === "nameShort") {
      setNameShort(impersonateClub.name || "")
    } else {
      setStarterKey(impersonateClub.starter_key || "")
    }

    setEditingField((current) => (current === field ? null : current))
  }

  useEffect(() => {
    if (!impersonateClub) return
    setNameLong(impersonateClub.nameLong || "")
    setNameShort(impersonateClub.name || "")
    setStarterKey(impersonateClub.starter_key || "")
  }, [impersonateClub])

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === "undefined") return
      setIsSmallScreen(window.innerWidth <= screenSizes.small)
    }

    handleResize()
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    if (!impersonateClub?.clubId) {
      setReportBrandingContext(null)
      setReportBrandingError(false)
      return
    }

    const currentClubId = Number(impersonateClub.clubId)

    const run = async () => {
      await loadReportBrandingContext(currentClubId)
    }

    run()
  }, [impersonateClub?.clubId])

  useEffect(() => {
    if (!impersonateClub?.clubId) {
      setIsGuestClub(false)
      return
    }
    const today = new Date()
    swimminglyApi
      .get(gen(`/api/getSeasonsForClub/${impersonateClub.clubId}`))
      .then((data: any) => {
        const seasons: any[] = data?.clubSeasons ?? []
        const guest = seasons.some(
          (s) =>
            s.isSwimminglyCustomer === 0 &&
            new Date(s.startDate) <= today &&
            new Date(s.endDate) >= today,
        )
        setIsGuestClub(guest)
      })
      .catch(() => setIsGuestClub(false))
  }, [impersonateClub?.clubId])

  useEffect(() => {
    if (!impersonateClub?.clubId) return

    const currentClubId = Number(impersonateClub.clubId)

    const handleTeamLogosChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ clubId?: number }>
      const changedClubId = Number(customEvent.detail?.clubId || 0)
      if (changedClubId !== currentClubId) return
      loadReportBrandingContext(currentClubId)
    }

    window.addEventListener(TEAM_LOGOS_CHANGED_EVENT, handleTeamLogosChanged)
    return () => {
      window.removeEventListener(TEAM_LOGOS_CHANGED_EVENT, handleTeamLogosChanged)
    }
  }, [impersonateClub?.clubId])

  useEffect(() => {
    const frontHeight = frontRef.current?.offsetHeight || 0
    const backHeight = backRef.current?.offsetHeight || 0
    const nextHeight = expanded ? backHeight : frontHeight

    if (nextHeight && nextHeight !== cardHeight) {
      setCardHeight(nextHeight)
    }
  }, [
    cardHeight,
    expanded,
    isSmallScreen,
    nameLong,
    nameShort,
    starterKey,
    reportBrandingContext,
    reportBrandingLoading,
  ])

  useEffect(() => {
    if (!expanded) return

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target as Node)) {
        if (editingField && impersonateClub) {
          resetField(editingField)
        }
        setExpanded(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [expanded, editingField, impersonateClub])

  const handleSave = async (field?: "nameLong" | "nameShort" | "starterKey") => {
    if (!impersonateClub?.clubId) return

    setSaving(true)
    try {
      let payloadNameLong = impersonateClub.nameLong || ""
      let payloadNameShort = impersonateClub.name || ""
      let payloadStarterKey = impersonateClub.starter_key || ""

      if (!field) {
        payloadNameLong = nameLong
        payloadNameShort = nameShort
        payloadStarterKey = starterKey
      } else if (field === "nameLong") {
        payloadNameLong = nameLong
      } else if (field === "nameShort") {
        payloadNameShort = nameShort
      } else {
        payloadStarterKey = starterKey
      }

      const result = await swimminglyApi
        .post(gen("/api/updateClubBasic"))
        .body({
          club_id: impersonateClub.clubId,
          club_name_long: payloadNameLong,
          club_name: payloadNameShort,
          starter_key: payloadStarterKey,
        })

      if (result?.club) {
        const updatedClubFromServer: any = result.club
        const mergedClub: any = {
          ...(impersonateClub as any),
          ...updatedClubFromServer,
        }

        if (updatedClubFromServer.name_long !== undefined) {
          mergedClub.nameLong = updatedClubFromServer.name_long
        }

        setAliasedClub(mergedClub)
        setEditingField(null)
        message.success("Updated club")
      } else {
        message.error("Unable to update club. Please try again.")
      }
    } catch (err) {
      console.error(err)
      message.error("Unable to update club. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  const handleInputKeyDown =
    (field: "nameLong" | "nameShort" | "starterKey") =>
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        e.preventDefault()
        e.stopPropagation()
        resetField(field)
        return
      }

      if (e.key === "Enter") {
        const nextValue = e.currentTarget.value
        if (!nextValue.trim()) {
          setShowErrors(true)
          return
        }

        e.preventDefault()
        handleSave(field)
      }
    }

  if (!user || user.role === 5) return null
  if (!impersonateClub) return null

  const approvedLogos = reportBrandingContext?.approvedLogos || []
  const hasMeasuredCardHeight = cardHeight !== null
  const preferredLogo =
    approvedLogos.find((logo) => logo.slotIndex === 0) ||
    approvedLogos.find((logo) => logo.slotIndex === 1) ||
    approvedLogos.find((logo) => logo.slotIndex === 2) ||
    null
  const teamLogosHref = gen(
    "/app/club/admin?tab=team-logos&intent=upload-logo&source=club-block",
  )

  return (
    <section className="panel panel-modern panel-club-summary">
      <div className="ourpanel-body">
        <ClubSummaryWrapper ref={containerRef}>
          <GearButton
            type="button"
            aria-label="Edit club summary"
            onClick={() => setExpanded((prev) => !prev)}
            alwaysVisible={expanded || isSmallScreen}
            disabled={saving}
          >
            <FontAwesomeIcon icon={faGear} />
          </GearButton>
          <CardFlipInner flipped={expanded} cardHeight={cardHeight}>
            <CardFace
              ref={frontRef as any}
              flipped={expanded}
              fixedHeight={hasMeasuredCardHeight}
            >
              <FrontFaceLayout>
                <LogoPanel>
                  {reportBrandingLoading ? (
                    <LogoStage empty={false}>
                      <LogoStageSkeleton>
                        <Skeleton.Image
                          active
                          style={{
                            width: "100%",
                            height: 92,
                            borderRadius: 10,
                          }}
                        />
                      </LogoStageSkeleton>
                    </LogoStage>
                  ) : reportBrandingError ? (
                    <LogoStage empty={false}>
                      <EmptyState>
                        <EmptyStateIcon>
                          <UploadOutlined />
                        </EmptyStateIcon>
                        <LogoUnavailableTitle>
                          Logo unavailable
                        </LogoUnavailableTitle>
                      </EmptyState>
                    </LogoStage>
                  ) : preferredLogo ? (
                    <LogoStage empty={false}>
                      <LogoImage
                        src={preferredLogo.dataUri}
                        alt={`${impersonateClub.nameLong || impersonateClub.name} ${preferredLogo.slotLabel}`}
                      />
                    </LogoStage>
                  ) : (
                    <LogoStage
                      as="a"
                      empty
                      clickable
                      href={teamLogosHref}
                      aria-label="Open Team Logos to upload a club logo"
                    >
                      <EmptyState>
                        <EmptyStateIcon>
                          <UploadOutlined />
                        </EmptyStateIcon>
                        <EmptyStateTitle>
                          Upload your team logo
                        </EmptyStateTitle>
                      </EmptyState>
                    </LogoStage>
                  )}
                </LogoPanel>
                <ClubSummaryContent>
                  <ClubSummaryLabel>Club summary</ClubSummaryLabel>
                  <StyledClubTable>
                    <tbody>
                      <tr>
                        <th>Club Name</th>
                        <th>Club Abbreviation</th>
                        <th>Club ID</th>
                        <th>Starter Key</th>
                      </tr>
                      <tr>
                        <td width="25%">{impersonateClub.nameLong}</td>
                        <td width="25%">{impersonateClub.name}</td>
                        <td width="25%">
                          {isGuestClub && user?.role !== 2 ? (
                            <Tooltip title="Not available for guest clubs">
                              <span style={{ filter: "blur(4px)", userSelect: "none", display: "inline-block" }}>
                                {impersonateClub.clubId}
                              </span>
                            </Tooltip>
                          ) : impersonateClub.clubId}
                        </td>
                        <td width="25%">
                          {isGuestClub && user?.role !== 2 ? (
                            <Tooltip title="Not available for guest clubs">
                              <span style={{ filter: "blur(4px)", userSelect: "none", display: "inline-block" }}>
                                {impersonateClub.starter_key || "—"}
                              </span>
                            </Tooltip>
                          ) : impersonateClub.starter_key}
                        </td>
                      </tr>
                    </tbody>
                  </StyledClubTable>
                  {isGuestClub && (
                    <div style={{ marginTop: 6 }}>
                      <Tag color="orange">Guest - Lite Access</Tag>
                    </div>
                  )}
                </ClubSummaryContent>
              </FrontFaceLayout>
            </CardFace>
            <CardFace
              back
              ref={backRef as any}
              flipped={expanded}
              fixedHeight={hasMeasuredCardHeight}
            >
              <ClubSummaryLabel>Club summary</ClubSummaryLabel>
              <InlineEditor>
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={6}>
                    <InlineField style={{ marginTop: 10 }}>
                      <InlineLabel>Club Name</InlineLabel>
                      {editingField === "nameLong" ? (
                        <InlineEditRow>
                          <Input
                            value={nameLong}
                            onChange={(e) => setNameLong(e.target.value)}
                            placeholder="Club Name"
                            status={showErrors && !nameLong.trim() ? "error" : undefined}
                            onKeyDown={handleInputKeyDown("nameLong")}
                            style={{ flex: 1 }}
                          />
                          <InlineIconButton
                            type="button"
                            aria-label="Cancel editing club name"
                            onClick={() => resetField("nameLong")}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </InlineIconButton>
                          <InlineIconButton
                            type="button"
                            aria-label="Save club name"
                            onClick={() => handleSave("nameLong")}
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </InlineIconButton>
                        </InlineEditRow>
                      ) : (
                        <InlineValueBox onClick={() => setEditingField("nameLong")}>
                          <span className="value-text">{nameLong || "Click to add"}</span>
                          <span className="edit-icon">
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </span>
                        </InlineValueBox>
                      )}
                      <InlineHelp>
                        Full club name as it should appear on reports.
                      </InlineHelp>
                    </InlineField>
                  </Col>
                  <Col xs={24} md={6}>
                    <InlineField style={{ marginTop: 10 }}>
                      <InlineLabel>Club Abbreviation</InlineLabel>
                      {editingField === "nameShort" ? (
                        <InlineEditRow>
                          <Input
                            value={nameShort}
                            onChange={(e) => setNameShort(e.target.value)}
                            placeholder="Club Abbreviation"
                            status={showErrors && !nameShort.trim() ? "error" : undefined}
                            onKeyDown={handleInputKeyDown("nameShort")}
                            style={{ flex: 1 }}
                          />
                          <InlineIconButton
                            type="button"
                            aria-label="Cancel editing club abbreviation"
                            onClick={() => resetField("nameShort")}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </InlineIconButton>
                          <InlineIconButton
                            type="button"
                            aria-label="Save club abbreviation"
                            onClick={() => handleSave("nameShort")}
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </InlineIconButton>
                        </InlineEditRow>
                      ) : (
                        <InlineValueBox onClick={() => setEditingField("nameShort")}>
                          <span className="value-text">{nameShort || "Click to add"}</span>
                          <span className="edit-icon">
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </span>
                        </InlineValueBox>
                      )}
                      <InlineHelp>Club abbreviation for heat sheet formatting.</InlineHelp>
                    </InlineField>
                  </Col>
                  <Col xs={24} md={12}>
                    <InlineHelpPill>
                      <Row gutter={[8, 0]}>
                        <Col xs={24} md={12}>
                          <InlineField>
                            <InlineLabel>Club ID</InlineLabel>
                            <InlineValueBox as="div" style={{ cursor: "not-allowed" }}>
                              {isGuestClub && user?.role !== 2 ? (
                                <Tooltip title="Not available for guest clubs">
                                  <span className="value-text" style={{ filter: "blur(4px)", userSelect: "none" }}>
                                    {impersonateClub.clubId}
                                  </span>
                                </Tooltip>
                              ) : (
                                <span className="value-text">{impersonateClub.clubId}</span>
                              )}
                              <span className="lock-icon">
                                <FontAwesomeIcon icon={faLock} />
                              </span>
                            </InlineValueBox>
                          </InlineField>
                        </Col>
                        <Col xs={24} md={12}>
                          <InlineField>
                            <InlineLabel>Starter Key</InlineLabel>
                            {isGuestClub && user?.role !== 2 ? (
                              <InlineValueBox as="div" style={{ cursor: "not-allowed" }}>
                                <Tooltip title="Not available for guest clubs">
                                  <span className="value-text" style={{ filter: "blur(4px)", userSelect: "none" }}>
                                    {starterKey || "—"}
                                  </span>
                                </Tooltip>
                                <span className="lock-icon">
                                  <FontAwesomeIcon icon={faLock} />
                                </span>
                              </InlineValueBox>
                            ) : editingField === "starterKey" ? (
                              <InlineEditRow>
                                <Input
                                  value={starterKey}
                                  onChange={(e) => setStarterKey(e.target.value)}
                                  placeholder="Starter Key"
                                  status={showErrors && !starterKey.trim() ? "error" : undefined}
                                  onKeyDown={handleInputKeyDown("starterKey")}
                                  style={{ flex: 1 }}
                                />
                                <InlineIconButton
                                  type="button"
                                  aria-label="Cancel editing starter key"
                                  onClick={() => resetField("starterKey")}
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </InlineIconButton>
                                <InlineIconButton
                                  type="button"
                                  aria-label="Save starter key"
                                  onClick={() => handleSave("starterKey")}
                                >
                                  <FontAwesomeIcon icon={faCheck} />
                                </InlineIconButton>
                              </InlineEditRow>
                            ) : (
                              <InlineValueBox onClick={() => setEditingField("starterKey")}>
                                <span className="value-text">{starterKey || "Click to add"}</span>
                                <span className="edit-icon">
                                  <FontAwesomeIcon icon={faPenToSquare} />
                                </span>
                              </InlineValueBox>
                            )}
                          </InlineField>
                        </Col>
                      </Row>
                      <CredentialsHint>
                        Credentials to login to the Scorekeeper&apos;s iPad.
                      </CredentialsHint>
                    </InlineHelpPill>
                  </Col>
                </Row>
              </InlineEditor>
            </CardFace>
          </CardFlipInner>
        </ClubSummaryWrapper>
      </div>
    </section>
  )
}
